// Backend · Alerts: the Rules tab — protected rules first, then custom rules,
// each row with its summary, explainer line and monitoring-mode control.

import { useEffect, useState } from 'react';
import { adminAlertById, adminAlertRules, adminAlerts } from '@/api/resources';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { usePixels } from '@/api/hooks';
import type { AlertMetricCatalogEntry, AlertRule, AlertRulesResponse } from '@/api/types';
import { queryClient } from '@/cache/queryClient';
import {
  activationHelpForRule,
  activationModeOf,
  activationOptionsForRule,
  campaignRunningCapability,
  campaignRunningUnavailableReason,
  type ActivationModeId,
} from '@/lib/alert-activation';
import { CAT_LABEL, summarizeRule } from '@/lib/alert-labels';
import { fmtRelative } from './admin-user-drawer';
import { ActivationSelector } from './backend-alerts-activation';
import { Badge, invalidate } from './backend-alerts-parts';

/** The accessible name of the rules list, which scrolls on its own. */
export const ALERT_RULE_LIST_LABEL = 'Alert rules';

export interface RulesTabProps {
  rules: readonly AlertRule[];
  metricCatalog?: readonly AlertMetricCatalogEntry[];
  explainers?: AlertRulesResponse['explainers'];
  canEdit: boolean;
  onEdit: (rule: AlertRule) => void;
}

interface ActivationVars {
  rule: AlertRule;
  activationMode: ActivationModeId;
}

export function RulesTab({ rules, metricCatalog = [], explainers = {}, canEdit, onEdit }: RulesTabProps) {
  // Rules and channels are configuration rows: the server bumps the
  // `configuration` revision when they change, so the declared effect is the
  // domain. These used to say `volatileResources: ['admin.alertRules']`, which
  // named a domain-backed resource the volatile invalidator skips by design —
  // the declaration was a no-op and nothing said so. The local invalidate
  // stays for immediacy; the domain is what reaches every other screen.
  const pixelsQuery = usePixels();
  const pixels = pixelsQuery.data ?? [];
  const pixelsLoaded = pixelsQuery.data !== undefined;
  const [activationDrafts, setActivationDrafts] = useState<Record<string, ActivationModeId>>({});
  const [activationPending, setActivationPending] = useState<Set<string>>(() => new Set());
  const [activationErrors, setActivationErrors] = useState<Record<string, unknown>>({});
  const activationM = useRegisteredMutation({
    domains: ['configuration'],
    mutationFn: ({ rule, activationMode }: ActivationVars) => api.patch(
      `/admin/alert-rules/${encodeURIComponent(rule.id)}/activation`,
      { body: { activation_mode: activationMode } },
    ),
    onSuccess: (_data, { activationMode }) => {
      invalidate(adminAlertRules.all);
      // Turning a rule Off resolves its open incidents in the same D1
      // transaction — refresh the Triggered tab too instead of letting it
      // show them until the next poll.
      if (activationMode === 'off') invalidate(adminAlerts.all, adminAlertById.all);
    },
  });
  useEffect(() => {
    setActivationDrafts((current) => {
      const next = { ...current };
      let changed = false;
      for (const rule of rules) {
        if (next[rule.id] === activationModeOf(rule)) {
          delete next[rule.id];
          changed = true;
        }
      }
      return changed ? next : current;
    });
    // A fresh rules payload also retires any stale failure banner — the rows
    // it described have been re-read from the server.
    setActivationErrors((current) => (Object.keys(current).length ? {} : current));
  }, [rules]);
  const setRuleActivation = async (rule: AlertRule, activationMode: ActivationModeId) => {
    const previous = activationDrafts[rule.id] ?? activationModeOf(rule);
    if (activationMode === previous || activationPending.has(rule.id)) return;
    setActivationDrafts((current) => ({ ...current, [rule.id]: activationMode }));
    setActivationErrors((current) => {
      if (!current[rule.id]) return current;
      const next = { ...current };
      delete next[rule.id];
      return next;
    });
    setActivationPending((current) => new Set(current).add(rule.id));
    try {
      // Await each row's own promise. Per-call `mutate` callbacks are only
      // guaranteed for the latest consecutive mutation, while operators may
      // change several rows before the first request finishes.
      await activationM.mutateAsync({ rule, activationMode });
      // The write landed. Refetch first, then drop the draft unconditionally,
      // so a server that settled on a different value (a concurrent operator)
      // cannot leave this row misreporting its mode forever.
      await queryClient.invalidateQueries({ queryKey: adminAlertRules.all });
      setActivationDrafts((current) => {
        const next = { ...current };
        delete next[rule.id];
        return next;
      });
    } catch (error) {
      setActivationDrafts((current) => {
        const next = { ...current };
        delete next[rule.id];
        return next;
      });
      setActivationErrors((current) => ({ ...current, [rule.id]: error }));
    } finally {
      setActivationPending((current) => {
        const next = new Set(current);
        next.delete(rule.id);
        return next;
      });
    }
  };
  const systemRules = rules.filter((r) => r.protected);
  const customRules = rules.filter((r) => !r.protected);
  const activationControl = (rule: AlertRule) => {
    const mode = activationDrafts[rule.id] ?? activationModeOf(rule);
    const pending = activationPending.has(rule.id);
    const error = activationErrors[rule.id];
    const options = activationOptionsForRule(rule, metricCatalog, {
      currentMode: mode,
      pixels,
      pixelsLoaded,
    });
    const capability = campaignRunningCapability(rule, metricCatalog, pixels, pixelsLoaded);
    const showCapabilityNote = mode === 'campaign_running' && capability !== 'supported';
    // Why Campaign running is struck out, in words under the selector rather
    // than only in the option's hover tooltip.
    const unavailableReason = campaignRunningUnavailableReason(rule, metricCatalog, { pixels, pixelsLoaded });
    const reasonId = `alert-rule-${rule.id}-activation-reason`;
    return (
      <div className="alert-rule-activation" style={{ display: 'grid', justifyItems: 'end', gap: 3 }} onClick={(event) => event.stopPropagation()}>
        <ActivationSelector
          value={mode}
          options={options}
          label={`Monitoring for ${rule.name}`}
          disabled={!canEdit}
          busy={pending}
          unavailableDescriptionId={unavailableReason ? reasonId : undefined}
          onChange={(nextMode) => void setRuleActivation(rule, nextMode)}
        />
        {showCapabilityNote ? (
          <span
            id={reasonId}
            role={capability === 'unsupported' ? 'alert' : undefined}
            className={capability === 'unsupported' ? undefined : 'muted'}
            style={{
              color: capability === 'unsupported' ? 'var(--warn)' : undefined,
              fontSize: 9.5,
              lineHeight: 1.3,
              maxWidth: 310,
              textAlign: 'right',
            }}
          >
            {activationHelpForRule(rule, metricCatalog, {
              currentMode: mode,
              pixels,
              pixelsLoaded,
            })}
          </span>
        ) : unavailableReason ? (
          <span id={reasonId} className="muted alert-activation-reason" style={{ fontSize: 9.5, lineHeight: 1.3, maxWidth: 310, textAlign: 'right' }}>
            {unavailableReason}
          </span>
        ) : null}
        {pending && <span className="muted mono" style={{ fontSize: 9 }} aria-live="polite">Saving…</span>}
        {error ? (
          <span role="alert" title={error instanceof Error ? error.message : String(error)} style={{ color: 'var(--bad)', fontSize: 9.5 }}>
            Update failed · restored
          </span>
        ) : null}
      </div>
    );
  };
  return (
    // The list scrolls on its own. For a role that cannot edit, nothing inside
    // it takes focus (the mode controls are disabled and there is no Edit
    // button), so the region itself is focusable and named: axe
    // scrollable-region-focusable, the 2026-10 route crawl (B9). An editor
    // reaches every rule by keyboard through its row's Edit button; a click
    // anywhere else on the row opens the same editor.
    <div className="alert-rule-list" role="region" aria-label={ALERT_RULE_LIST_LABEL} tabIndex={0} style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' }}>
      {/* Protected rules keep a fixed definition and scope. Monitoring remains
          operator-controlled; metric rules additionally expose thresholds. */}
      {systemRules.length > 0 && (
        <>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)', margin: '2px 2px 4px' }}>
            Protected rules — structure locked
          </div>
          {systemRules.map((r) => (
            <div key={r.id} className={`panel alert-rule-card${(activationDrafts[r.id] ?? activationModeOf(r)) === 'off' ? ' alert-rule-off' : ''}`} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, cursor: canEdit ? 'pointer' : 'default' }}
              onClick={() => canEdit && onEdit(r)}>
              <span title="Protected rule" style={{ color: 'var(--ink-3)', fontSize: 12 }}>🔒</span>
              <div className="alert-rule-copy" style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                <div className="muted mono" style={{ fontSize: 11 }}>{summarizeRule(r, metricCatalog)}</div>
                {explainers[r.id]?.summary && (
                  <div className="field-hint" style={{ marginTop: 2 }}>{explainers[r.id]?.summary}</div>
                )}
              </div>
              {r.last_triggered_at && <span className="muted" style={{ fontSize: 10.5 }}>fired {fmtRelative(+new Date(r.last_triggered_at))}</span>}
              {activationControl(r)}
              {canEdit && <EditRuleButton rule={r} onEdit={onEdit}/>}
            </div>
          ))}
          <div style={{ height: 8 }} />
        </>
      )}

      {customRules.length === 0 && systemRules.length === 0 ? (
        <div className="muted" style={{ padding: '40px 12px', fontSize: 13, fontStyle: 'italic', textAlign: 'center' }}>
          No alert rules yet.{canEdit ? ' Create one to start monitoring.' : ''}
        </div>
      ) : (
        customRules.map((r) => (
          <div key={r.id} className={`panel alert-rule-card${(activationDrafts[r.id] ?? activationModeOf(r)) === 'off' ? ' alert-rule-off' : ''}`} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, cursor: canEdit ? 'pointer' : 'default' }}
            onClick={() => canEdit && onEdit(r)}>
            <div className="alert-rule-copy" style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
              <div className="muted mono" style={{ fontSize: 11 }}>{summarizeRule(r, metricCatalog)}</div>
              {explainers[r.id]?.summary && (
                <div className="field-hint" style={{ marginTop: 2 }}>{explainers[r.id]?.summary}</div>
              )}
            </div>
            <Badge color="var(--ink-3)">{CAT_LABEL[r.category] ?? r.category}</Badge>
            {r.last_triggered_at && <span className="muted" style={{ fontSize: 10.5 }}>fired {fmtRelative(+new Date(r.last_triggered_at))}</span>}
            {activationControl(r)}
            {canEdit && <EditRuleButton rule={r} onEdit={onEdit}/>}
          </div>
        ))
      )}
    </div>
  );
}

/**
 * A row's Edit button, at the row's end on every width. The row itself takes a
 * click but no focus, so this is how the keyboard reaches each rule; its name
 * says which rule it opens.
 */
function EditRuleButton({ rule, onEdit }: { rule: AlertRule; onEdit: (rule: AlertRule) => void }) {
  return (
    <button
      type="button"
      className="btn alert-rule-details"
      aria-label={`Edit rule ${rule.name}`}
      onClick={(event) => { event.stopPropagation(); onEdit(rule); }}
    >
      Edit
    </button>
  );
}
